/**
 * Consent dismiss cookie names used across brands (see ADR-004).
 * Read paths must check every name a visitor may have from legacy or current writes.
 */
export const CONSENT_BANNER_COOKIE_NAMES = [
  'achieve_tracking_consent_banner',
  'tracking_consent_banner',
] as const

function parseCookieNames(cookieString: string): Set<string> {
  const names = new Set<string>()
  if (!cookieString) return names
  for (const segment of cookieString.split(';')) {
    const trimmed = segment.trim()
    if (!trimmed) continue
    const eq = trimmed.indexOf('=')
    if (eq === -1) continue
    names.add(trimmed.slice(0, eq).trim())
  }
  return names
}

/** True when the Cookie header (or document.cookie) contains any consent dismiss cookie. */
export function cookieStringHasConsentBanner(cookieString: string): boolean {
  const names = parseCookieNames(cookieString)
  return CONSENT_BANNER_COOKIE_NAMES.some((name) => names.has(name))
}

/** Pre-paint check embedded in CONSENT_INLINE_SCRIPT — must match parseCookieNames semantics. */
function buildInlineHasConsentCookieExpression(): string {
  const namesLiteral = JSON.stringify([...CONSENT_BANNER_COOKIE_NAMES])
  return `(function(c){
    var names = ${namesLiteral};
    var parts = c.split(';');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (!part) continue;
      var eq = part.indexOf('=');
      if (eq === -1) continue;
      var n = part.slice(0, eq).trim();
      for (var j = 0; j < names.length; j++) {
        if (n === names[j]) return true;
      }
    }
    return false;
  })(document.cookie)`
}

/**
 * Inline script in root layout: sync ack class + pre-hydration dismiss handler.
 * Pre-paint cookie detection uses the same token parsing as cookieStringHasConsentBanner().
 */
export const CONSENT_INLINE_SCRIPT = `
(function(){
  if (${buildInlineHasConsentCookieExpression()}) {
    document.documentElement.classList.add('consent-acknowledged');
  }
  document.addEventListener('click', function(e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-consent-dismiss]');
    if (!btn) return;
    var name = btn.getAttribute('data-cookie-name');
    var domain = btn.getAttribute('data-cookie-domain');
    if (!name) return;
    var cookie = name + '=1; path=/; max-age=315360000; SameSite=Lax';
    if (domain) cookie += '; domain=' + domain;
    if (location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
    document.documentElement.classList.add('consent-acknowledged');
    window.__consentDismissedBeforeHydration = btn.getAttribute('aria-label') === 'Close' ? 'close' : 'ok';
  }, true);
})();
`
